import { useTranslation } from "react-i18next";
import { cn } from "@nexus/ui";
import { Tooltip, TooltipContent, TooltipTrigger } from "@nexus/ui";
import { ImagePreviewTrigger } from "../ImagePreviewTrigger";
import { OrgLogoMark } from "../OrgLogoMark";

export interface OrgChipTenant {
  id: string;
  name: string;
  logoUrl?: string | null;
}

interface OrgChipsProps {
  tenants: OrgChipTenant[];
  highlightId?: string | null;
  max?: number;
}

export function OrgChips({ tenants, highlightId, max = 4 }: OrgChipsProps) {
  const { t } = useTranslation();
  const visible = tenants.slice(0, max);
  const extra = tenants.length - max;
  const hasMatch = !!highlightId && tenants.some((tenant) => tenant.id === highlightId);

  return (
    <div className="flex flex-wrap items-center gap-0.5">
      {visible.map((tenant) => {
        const isMatch = hasMatch && tenant.id === highlightId;
        return (
          <Tooltip key={tenant.id}>
            <TooltipTrigger asChild>
              <span
                className={cn(
                  "inline-flex rounded transition-opacity duration-150 ease-out",
                  isMatch && "ring-2 ring-primary",
                  hasMatch && !isMatch && "opacity-40",
                )}
              >
                <ImagePreviewTrigger src={tenant.logoUrl} alt={tenant.name}>
                  <OrgLogoMark name={tenant.name} logoUrl={tenant.logoUrl} size={20} />
                </ImagePreviewTrigger>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {isMatch ? t("iam.dashboard.people.orgChips.matchesFilter", { name: tenant.name }) : tenant.name}
            </TooltipContent>
          </Tooltip>
        );
      })}
      {extra > 0 && (
        <span className={cn(
          "inline-flex h-[20px] min-w-[20px] items-center justify-center rounded-full bg-muted px-1.5 text-[10px] font-medium text-muted-foreground transition-opacity duration-150 ease-out",
          hasMatch && "opacity-40",
        )}>
          +{extra}
        </span>
      )}
    </div>
  );
}
